import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import React from 'react'
import { formatDate } from '~/src/libs/formatDate'
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined'
import { FormatText } from '~/src/utils/formatText'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import LastPageIcon from '@mui/icons-material/LastPage'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'

interface ITableContainerUserID {
  users: IUser[]
}
interface IUser {
  manager: string
  name: string
  date: string
  contents: string
}
interface Column {
  id: string
  label: string
  minWidth?: number | string
  align?: 'right' | 'left' | 'center'
  alignItem?: 'right' | 'left' | 'center'
  render: (item: IUser, index: number) => React.ReactNode
}

interface TablePaginationProps {
  count: number
  page: number
  rowsPerPage: number
  onPageChange: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void
}

function TablePaginationActions(props: TablePaginationProps) {
  const theme = useTheme()
  const { count, page, rowsPerPage, onPageChange } = props

  const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, 0)
  }

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1)
  }

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1)
  }

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
  }

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  )
}

export const TableContainerUserID: React.FC<ITableContainerUserID> = ({ users }): JSX.Element => {
  const { t } = useTranslation()

  const columns: readonly Column[] = [
    {
      id: 'date',
      label: `${t('support_history_user_id.date')}`,
      minWidth: '154px',
      render: ({ date }) => (
        <Typography
          variant={'body2'}
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {formatDate(date)}
        </Typography>
      ),
    },
    {
      id: 'name',
      label: `${t('support_history_user_id.member_name')}`,
      minWidth: '174px',
      render: ({ name }) => (
        <Typography
          variant={'body2'}
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {name}
        </Typography>
      ),
    },
    {
      id: 'manager',
      label: `${t('support_history_user_id.manager')}`,
      minWidth: '174px',
      align: 'left',
      alignItem: 'right',
      render: ({ manager }) => (
        <Typography
          variant={'body2'}
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {manager}
        </Typography>
      ),
    },
    {
      id: 'contents',
      label: `${t('support_history_user_id.content')}`,
      minWidth: '174px',
      align: 'left',
      alignItem: 'right',
      render: ({ contents }) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant={'body2'}
            sx={{
              fontFamily: 'Roboto',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.15px',
              color: 'rgba(0, 0, 0, 0.87)',
            }}
          >
            {FormatText(contents, 20)}
          </Typography>
          <IconButton aria-label="fingerprint">
            <InsertDriveFileOutlined />
          </IconButton>
        </Box>
      ),
    },
  ]
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(10)
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }
  return (
    <TableContainer sx={{ paddingX: '8px' }} component={Paper}>
      <Table
        sx={{ maxHeight: 440 }}
        aria-label="custom pagination table sticky table"
        aria-labelledby="tableTitle"
        size={'medium'}
        stickyHeader
      >
        <TableHead sx={{ backgroundColor: 'white' }}>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell
                key={column.id}
                sortDirection={'desc'}
                style={{
                  minWidth: column.minWidth,
                  textAlign: column.align,
                  backgroundColor: 'white',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 16,
                      lineHeight: '24px',
                      fontFamily: 'Roboto',
                      fontStyle: 'normal',
                      fontWeight: `${index === 3 ? 'bold' : 500}`,
                    }}
                  >
                    {column.label}
                  </Typography>
                  <Typography
                    sx={{
                      height: '14px',
                      width: '2px',
                      backgroundColor: `${index === columns.length - 1 ? 'white' : '#E0E0E0'}`,
                    }}
                  />
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => {
            return (
              <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                {columns.map((column) => (
                  <TableCell key={column.id} style={{ textAlign: column.align, padding: '6px' }}>
                    {column.render(row, index)}
                  </TableCell>
                ))}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      <TableFooter sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}>
        <TableRow>
          <TablePagination
            rowsPerPageOptions={[]}
            count={users.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              fontSize: 12,
              fontStyle: 'normal',
              lineHeight: '19.92px',
              fontWeight: 400,
              borderColor: 'white',
            }}
            SelectProps={{
              inputProps: {
                'aria-label': 'rows per page',
              },
              native: true,
            }}
            ActionsComponent={TablePaginationActions}
          />
        </TableRow>
      </TableFooter>
    </TableContainer>
  )
}
